import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { ChevronDown, Search, HelpCircle } from 'lucide-react';

function FAQItem({ item, isOpen, onToggle }) {
  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${isOpen ? 'border-[#CC0000] shadow-sm' : 'border-gray-200'}`}
      data-testid={`faq-item-${item.id}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors">
        <span className={`font-medium text-sm md:text-base pr-4 ${isOpen ? 'text-[#CC0000]' : 'text-gray-900'}`}>
          {item.question}
        </span>
        <ChevronDown size={18} className={`flex-shrink-0 transition-transform text-gray-400 ${isOpen ? 'rotate-180 text-[#CC0000]' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 bg-white border-t border-gray-100">
          <p className="text-sm text-gray-600 leading-relaxed pt-3">{item.answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const { t } = useLanguage();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);
  const [catFilter, setCatFilter] = useState('');

  useEffect(() => {
    api.get('/faqs').then(r => setFaqs(r.data || [])).finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(faqs.map(f => f.category))];
  const filtered = faqs.filter(f => {
    const matchSearch = !search || f.question.toLowerCase().includes(search.toLowerCase()) || f.answer.toLowerCase().includes(search.toLowerCase());
    const matchCat = !catFilter || f.category === catFilter;
    return matchSearch && matchCat;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-10 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mb-4">
            <HelpCircle size={24} className="text-[#CC0000]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t('faq.title')}</h1>
          <p className="text-gray-500 text-sm md:text-base">{t('faq.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('faq.search_placeholder')} data-testid="faq-search"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white" />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} data-testid="faq-category-filter"
            className="border border-gray-300 rounded-md text-sm py-2.5 px-3 bg-white focus:ring-2 focus:ring-red-500 outline-none">
            <option value="">{t('faq.all_categories')}</option>
            {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="space-y-3">{[...Array(5)].map(i => <div key={i} className="h-14 bg-white rounded-lg animate-pulse" />)}</div>
        ) : (
          <>
            <div className="space-y-3" data-testid="faq-list">
              {filtered.map(f => (
                <FAQItem key={f.id} item={f} isOpen={openId === f.id} onToggle={() => setOpenId(openId === f.id ? null : f.id)} />
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-10 text-gray-400">
                  <p>{t('faq.no_results')}</p>
                </div>
              )}
            </div>
            <div className="mt-10 bg-[#CC0000] text-white rounded-xl p-6 text-center">
              <h3 className="font-bold text-lg mb-2">{t('faq.not_found')}</h3>
              <p className="text-red-100 text-sm mb-4">{t('faq.not_found_desc')}</p>
              <Link to="/contactos" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#CC0000] font-semibold rounded-md hover:bg-red-50 transition-colors text-sm">
                {t('faq.contact_embassy')}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
